import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity } from 'lucide-react';
import Modal from '../ui/Modal';
import { adminService } from '../../services/adminService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
  userLabel?: string;
}

interface LogRow {
  id: string;
  action: string;
  description?: string | null;
  entity_type?: string | null;
  entity_id?: string | null;
  ip_address?: string | null;
  created_at: string;
  user?: { email?: string; first_name?: string; last_name?: string };
}

const actionTone = (action: string): string => {
  if (action.includes('canceled') || action.includes('deactivated') || action.includes('deleted')) return 'text-red-600 dark:text-red-400';
  if (action.includes('reactivated') || action.includes('granted') || action.includes('assigned') || action.includes('created')) return 'text-green-600 dark:text-green-400';
  if (action.includes('changed') || action.includes('updated') || action.includes('extended')) return 'text-blue-600 dark:text-blue-400';
  return 'text-gray-700 dark:text-gray-300';
};

const UserAuditLogModal: React.FC<Props> = ({ isOpen, onClose, userId, userLabel }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !userId) return;
    let cancelled = false;
    setIsLoading(true);
    adminService.getAuditLogs({ user_id: userId, limit: 50 })
      .then((res) => { if (!cancelled && res.success && res.data) setLogs(res.data as LogRow[]); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, userId]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('admin.auditLogTitle')} size="2xl" maxHeight>
      <div className="space-y-3">
        {userLabel && (
          <p className="text-sm text-gray-600 dark:text-gray-400">{userLabel}</p>
        )}
        {isLoading ? (
          <div className="space-y-2">
            {[1,2,3,4,5].map(i => <div key={i} className="h-12 skeleton rounded-lg" />)}
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Activity className="w-10 h-10 mx-auto mb-3 opacity-50" />
            {t('admin.auditLogEmpty')}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {logs.map((log) => (
              <li key={log.id} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium ${actionTone(log.action)}`}>{log.action}</p>
                    {log.description && <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 break-words">{log.description}</p>}
                    {log.entity_type && (
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {log.entity_type}{log.entity_id ? ` · ${log.entity_id.slice(0, 8)}` : ''}
                        {log.ip_address ? ` · ${log.ip_address}` : ''}
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString('pt-BR')}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
};

export default UserAuditLogModal;
